import { useEffect, useState } from 'react'
import { Box, CircularProgress, LinearProgress, TextField, Tooltip, Typography } from '@mui/material'
import { ArrowDown, ArrowUp, Copy, Search, TriangleAlert } from 'lucide-react'
import { useNavigate } from '../../lib/router'
import type { Benchmark, RegionField } from '../../api/types'
import { useRegionFields } from '../../api/hooks'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '../../components/primitives'
import { Skeleton, SkeletonGridRows } from '../../components/Skeleton'
import { ha, num } from '../../lib/format'
import { tokens, type PlotStatus } from '../../theme'

interface Column {
  key: string
  label: string
  width: string
  /** Only the columns the endpoint allows sorting on. */
  sortable?: boolean
  align?: 'right'
}

// Sized to fit beside the 340px panel at 1440 without a horizontal scroll: the
// yield and its distance from the benchmark are the point of the screen and
// must not be the columns that fall off the edge.
const COLUMNS: Column[] = [
  { key: 'plot_id', label: 'Plot_ID', width: '150px', sortable: true },
  { key: 'fo', label: 'Producer', width: 'minmax(120px,1fr)', sortable: true },
  { key: 'farmer_id', label: 'Farmer_ID', width: '112px', sortable: true },
  { key: 'area_ha', label: 'Area', width: '80px', sortable: true, align: 'right' },
  { key: 'production_kg', label: 'Declared', width: '96px', sortable: true, align: 'right' },
  { key: 'kg_ha', label: 'Yield', width: '100px', sortable: true, align: 'right' },
  { key: 'pct', label: 'vs FAO', width: '80px', sortable: true, align: 'right' },
  { key: 'verdict', label: 'Verdict', width: '100px' },
]

const GRID = COLUMNS.map((c) => c.width).join(' ')
const PAGE = 100

/**
 * Every field in the region and what it declared.
 *
 * Area sits next to the yield deliberately: a five-figure kg/ha on a 0.02 ha
 * polygon is the polygon's fault, not the farmer's, and the two numbers have to
 * be read together for that to be obvious.
 */
export function FieldsTable({
  regionId, season, benchmark, threshold, window, fo, farmerId, suspicious, onToggleSuspicious,
}: {
  regionId: string
  /** Harvest season to read; undefined for the latest declared. */
  season?: string
  benchmark: Benchmark | null
  threshold: number
  window: 5 | 10
  fo: string | null
  farmerId: string | null
  suspicious: boolean
  onToggleSuspicious: () => void
}) {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  // File order by default. Opening on a descending yield sort shows only the
  // tail, which makes a normal dataset look uniformly fraudulent.
  const [sort, setSort] = useState('ordinal')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')

  const { data, isFetching, isPlaceholderData, isError, error } = useRegionFields(regionId, {
    season,
    threshold: String(threshold),
    window: String(window),
    fo: fo ?? undefined,
    farmer_id: farmerId ?? undefined,
    suspicious: suspicious ? 'true' : undefined,
    q: search.trim() || undefined,
    sort,
    dir,
    limit: String(PAGE),
    offset: String(page * PAGE),
  })

  // Another season is another list: back to its first page.
  useEffect(() => setPage(0), [season])
  // The rows on screen belong to the previous request (another season, page
  // or filter) until this one answers: shown faded rather than swapped for a
  // skeleton, so the screen does not jump — but never passed off as current.
  const stale = isPlaceholderData

  const rows = data?.fields ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))

  const toggleSort = (key: string) => {
    setPage(0)
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc')
    else {
      setSort(key)
      // Worst first when sorting by a yield measure, ascending otherwise.
      setDir(key === 'kg_ha' || key === 'pct' || key === 'production_kg' ? 'desc' : 'asc')
    }
  }

  const cell = {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    borderTop: `1px solid ${tokens.color.ink200}`,
    minWidth: 0,
    opacity: stale ? 0.35 : 1,
    transition: `opacity ${tokens.motion.dur.fast}ms`,
  }

  const scope = [fo, farmerId].filter(Boolean).join(' · ')

  return (
    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', px: 6, py: 6, gap: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6 }}>
        <Box sx={{ minWidth: 0 }}>
          <Eyebrow sx={{ mb: 2 }}>
            {suspicious ? `Over ${num(threshold)}% above the FAO mean` : scope || 'All fields'}
          </Eyebrow>
          {/* Not "0 fields" until the count is known — that reads as an
              answer, and it was the wrong one. */}
          {data || isError ? (
            <Typography variant="h1">
              {num(total)} {total === 1 ? 'field' : 'fields'}
            </Typography>
          ) : (
            <Skeleton width={180} height={30} radius={tokens.radius.md} sx={{ my: 1 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 'none' }}>
          <Box
            component="button"
            type="button"
            aria-pressed={suspicious}
            onClick={() => {
              setPage(0)
              onToggleSuspicious()
            }}
            sx={{
              height: 36, px: 3, cursor: 'pointer', borderRadius: `${tokens.radius.md}px`,
              border: `1px solid ${suspicious ? tokens.color.warning : tokens.color.ink300}`,
              backgroundColor: suspicious ? tokens.color.tintWarning : 'background.paper',
              color: suspicious ? tokens.color.warning : 'text.primary',
              font: `500 13px ${tokens.font.sans}`, whiteSpace: 'nowrap',
            }}
          >
            {suspicious ? 'Showing suspicious only' : 'Suspicious only'}
          </Box>
          <TextField
            size="small"
            value={search}
            placeholder="Plot, farmer, organisation"
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            inputProps={{ 'aria-label': 'Search fields' }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', pr: 2, color: 'text.disabled' }}>
                  <Search size={16} strokeWidth={1.5} />
                </Box>
              ),
            }}
            sx={{ width: 280 }}
          />
        </Box>
      </Box>

      <Surface sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <LinearProgress sx={{ height: 2, flex: 'none', visibility: stale ? 'visible' : 'hidden' }} />
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 2.5, px: 5, minWidth: 960 }}>
            {COLUMNS.map((c) => (
              <Box
                key={c.key}
                component={c.sortable ? 'button' : 'div'}
                type={c.sortable ? 'button' : undefined}
                onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, height: 36, border: 0, p: 0,
                  background: 'none', textAlign: 'left',
                  justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                  cursor: c.sortable ? 'pointer' : 'default',
                  position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: sort === c.key ? 'text.primary' : 'text.disabled',
                }}
              >
                {c.label}
                {sort === c.key &&
                  (dir === 'asc' ? <ArrowUp size={11} strokeWidth={2} /> : <ArrowDown size={11} strokeWidth={2} />)}
              </Box>
            ))}

            {/* First load only; a refetch keeps the rows already shown. */}
            {!data && !isError && (
              <SkeletonGridRows
                columns={COLUMNS.length}
                rows={10}
                height={44}
                align={COLUMNS.map((c, i) => (c.align === 'right' ? i : -1)).filter((i) => i >= 0)}
              />
            )}
            {rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                cell={cell}
                onOpen={row.run_id
                  ? () => navigate(`/checks/runs/${row.run_id}/plots/${row.id}`)
                  : undefined}
              />
            ))}
          </Box>

          {/*
            A failed request is not an empty result. Showing "No fields
            match" for a 500 sent somebody looking for a filter to clear
            while the server was telling us what was actually wrong.
          */}
          {isError && (
            <EmptyState
              title="Could not load these fields"
              body={(error as Error)?.message ?? 'The server did not answer.'}
            />
          )}
          {data && !isError && !isFetching && rows.length === 0 && (
            <EmptyState
              title="No fields match"
              body="Clear the search, the producer or the suspicious filter to see the rest of this region."
            />
          )}
        </Box>

        <Box
          sx={{
            flex: 'none', height: 48, borderTop: `1px solid ${tokens.color.ink200}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {isFetching ? (
              <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
            ) : benchmark?.mean_yield ? (
              `${num(page * PAGE + 1)}–${num(Math.min((page + 1) * PAGE, total))} of ${num(total)} · benchmark ${num(benchmark.mean_yield, 1)} kg/ha`
            ) : (
              `${num(page * PAGE + 1)}–${num(Math.min((page + 1) * PAGE, total))} of ${num(total)}`
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <PageButton disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</PageButton>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {num(page + 1)} / {num(pages)}
            </Typography>
            <PageButton disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</PageButton>
          </Box>
        </Box>
      </Surface>
    </Box>
  )
}

function Row({ row, cell, onOpen }: { row: RegionField; cell: object; onOpen?: () => void }) {
  const text = (v: unknown, mono = false) => (
    <Box
      sx={{
        ...cell,
        font: `400 13px ${mono ? tokens.font.mono : tokens.font.sans}`,
        color: v === null || v === undefined || v === '' ? 'text.disabled' : 'text.primary',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
        lineHeight: '44px',
      }}
    >
      {v === null || v === undefined || v === '' ? '—' : String(v)}
    </Box>
  )
  const right = (v: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
  )

  const delta = row.pct_vs_benchmark

  return (
    <Box
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => e.key === 'Enter' && onOpen?.()}
      sx={{
        display: 'contents',
        cursor: onOpen ? 'pointer' : 'default',
        '&:hover > *': { backgroundColor: tokens.color.ink50 },
      }}
    >
      <Box sx={{ ...cell, gap: 1.5 }}>
        <Mono sx={{ fontSize: 13, fontWeight: 500 }}>{row.plot_ref ?? '—'}</Mono>
        {row.status !== 'passed' && <PolygonMark status={row.status} />}
        {row.duplicated_claim && (
          <Tooltip title="This plot reference is used by more than one polygon, so the same harvest is claimed twice">
            <Box sx={{ display: 'flex', color: tokens.color.warning, flex: 'none' }}>
              <Copy size={12} strokeWidth={2} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {text(row.fo)}
      {text(row.farmer_id, true)}
      {right(<Typography variant="body2" sx={{ color: 'text.primary' }}>{ha(row.area_ha)}</Typography>)}
      {right(
        <Typography variant="body2" sx={{ color: row.production_kg === null ? 'text.disabled' : 'text.primary' }}>
          {row.production_kg === null ? '—' : `${num(row.production_kg)} kg`}
        </Typography>,
      )}
      {right(
        <Typography
          sx={{
            font: `${row.over_threshold ? 500 : 400} 13px ${tokens.font.sans}`,
            color: row.kg_ha === null ? 'text.disabled' : row.over_threshold ? tokens.color.warning : 'text.primary',
          }}
        >
          {row.kg_ha === null ? '—' : `${num(row.kg_ha, 0)} kg/ha`}
        </Typography>,
      )}
      {right(
        <Typography
          sx={{
            font: `400 13px ${tokens.font.sans}`,
            color: delta === null
              ? 'text.disabled'
              : row.over_threshold ? tokens.color.warning : 'text.secondary',
          }}
        >
          {delta === null ? '—' : `${delta > 0 ? '+' : ''}${num(delta, 0)}%`}
        </Typography>,
      )}
      <Box sx={cell}>
        {/* The polygon status next door answers a different question, so the
            yield verdict is stated outright rather than left to a colour. */}
        {row.unreported ? (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>not reported</Typography>
        ) : row.over_threshold ? (
          <StatusPill status="flagged" label="Suspicious" />
        ) : (
          <StatusPill status="passed" label="Plausible" />
        )}
      </Box>
    </Box>
  )
}

/**
 * The polygon's own verdict, where it bears on the yield.
 *
 * This column used to be a status pill beside the yield verdict, and two
 * pills a few pixels apart were read as one answer: "Passed" next to
 * "Plausible" looked like the yield had been checked twice, and a polygon
 * the checks rejected read as a yield that was fine. So the good case says
 * nothing at all, and only a polygon that undermines the figure is marked.
 *
 * It matters because the yield is production ÷ area: if the geometry is
 * wrong, the kg/ha is wrong with it, however plausible the number looks.
 */
const POLYGON_WARNING: Record<Exclude<PlotStatus, 'passed'>, string> = {
  rejected:
    'The checks rejected this polygon. Yield is production ÷ area, so its '
    + 'area — and this kg/ha with it — cannot be relied on. Open the field to see why.',
  flagged:
    'The checks flagged this polygon. Its area may be wrong, and the yield per '
    + 'hectare changes with it. Open the field to see why.',
  excluded:
    'This polygon was excluded from the region, so it is not part of the '
    + 'submission even though its declaration is listed here.',
}

function PolygonMark({ status }: { status: Exclude<PlotStatus, 'passed'> }) {
  const colour = status === 'rejected' ? tokens.color.danger
    : status === 'flagged' ? tokens.color.warning
    : tokens.color.ink400
  return (
    <Tooltip title={POLYGON_WARNING[status]}>
      <Box
        aria-label={`Polygon ${status}`}
        sx={{ display: 'flex', color: colour, flex: 'none' }}
      >
        <TriangleAlert size={12} strokeWidth={2} />
      </Box>
    </Tooltip>
  )
}

function PageButton({ children, disabled, onClick }: { children: React.ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        height: 28, px: 2.5, borderRadius: `${tokens.radius.md}px`,
        border: `1px solid ${tokens.color.ink300}`, backgroundColor: 'background.paper',
        font: `500 12px ${tokens.font.sans}`,
        color: disabled ? 'text.disabled' : 'text.primary',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </Box>
  )
}
